import { useCallback, useEffect, useState } from "react";
import { guestApi } from "@/lib/api";

const USER_KEY = "stayeasy_user";
const CHANGE_EVENT = "stayeasy:session-change";

const readUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeUser = (profile) => {
  if (!profile) {
    localStorage.removeItem(USER_KEY);
    return null;
  }

  const record = {
    id: profile.id,
    name: profile.name || "Guest",
    email: profile.email || "",
    phone: profile.phone || "",
    emailVerified: profile.emailVerified ?? profile.email_verified_at != null,
    notificationsAll: profile.notificationsAll ?? profile.email_notifications_enabled ?? true,
    notificationsChat: profile.notificationsChat ?? profile.email_notify_booking_chat ?? true,
    avatar: profile.avatar || profile.avatar_url || "",
  };

  localStorage.setItem(USER_KEY, JSON.stringify(record));
  return record;
};

const broadcast = () => window.dispatchEvent(new Event(CHANGE_EVENT));

export const useSession = () => {
  const [user, setUser] = useState(readUser);
  const [checking, setChecking] = useState(() => !!readUser());

  const refresh = useCallback(async () => {
    try {
      const response = await guestApi.me();
      const profile =
        response?.unwrapped?.user ||
        response?.unwrapped ||
        response?.data?.user ||
        response?.data;
      const record = writeUser(profile);
      setUser(record);
      return record;
    } catch (error) {
      if ([401, 419].includes(error?.response?.status)) {
        writeUser(null);
        setUser(null);
      }
      return null;
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    // A guest who has never logged in has no local session marker. Avoid
    // probing /guest/me on every public-page mount because a 401 is the
    // expected response for anonymous visitors and only creates console noise.
    if (!readUser()) {
      setChecking(false);
      return;
    }

    refresh();
  }, [refresh]);

  useEffect(() => {
    const onChange = () => setUser(readUser());
    window.addEventListener("storage", onChange);
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => {
      window.removeEventListener("storage", onChange);
      window.removeEventListener(CHANGE_EVENT, onChange);
    };
  }, []);

  const login = useCallback((profile) => {
    const record = writeUser(profile);
    setUser(record);
    broadcast();
    return record;
  }, []);

  const logout = useCallback(async () => {
    try {
      await guestApi.logout();
    } catch (_) {}
    writeUser(null);
    setUser(null);
    broadcast();
  }, []);

  const updateProfile = useCallback((patch) => {
    const next = writeUser({ ...(readUser() || {}), ...patch });
    setUser(next);
    broadcast();
    return next;
  }, []);

  return {
    user,
    isAuthenticated: !!user,
    checking,
    login,
    logout,
    updateProfile,
    refresh,
  };
};

export default useSession;
